import { ReactNode, useEffect, useMemo, useState } from 'react';

import { Button } from '@components/button';
import { Card } from '@components/card';
import { useDebouncedValue } from '@components/component-helpers';
import { EmptySearchState } from '@components/empty-search-state';
import { Form } from '@components/form';
import { COLOR_TO_VARIANT, ICON_CHOOSER_ICONS } from '@components/icon-chooser/icon-chooser';
import { IconFancyContainer } from '@components/icon-fancy-container';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconVisibilityHidden } from '@components/icons/visibility-hidden';
import { IconVisibilityShown } from '@components/icons/visibility-shown';
import { InfoTooltip } from '@components/info-tooltip';
import { Spinner } from '@components/loading-spinner';
import { SearchBox } from '@components/search-box';
import { Separator } from '@components/separator';
import { hideSidebar, Sidebar, useSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { Color } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { AttributeFieldType, AttributeType } from 'api/attributes/requests';
import { useJourney, useJourneys } from 'api/journeys/hooks';
import {
  useCreateMCPServerJourney,
  useMCPServerJourneys,
  useUpdateMCPServerJourney,
} from 'api/mcp/hooks';
import { MCPServerJourney } from 'api/mcp/requests';
import { JourneyType } from 'api/types';
import { useWorkspaces } from 'api/workspaces/hooks';
import { Workspace } from 'api/workspaces/requests';
import { AttributeIcon } from 'ui/authenticated/settings/pages/attributes/common';

import { useAttributeModal } from './attribute-modal';

export const useMCPServerJourneySidebar = () =>
  useSidebar(MCPServerJourneySidebar, {
    variant: 'CONTENT_HEAVY',
    id: 'mcp-server-journey-sidebar',
  });

const SectionLayout = ({ title, children }: { title: string; children: ReactNode }) => (
  <Stack orientation="horizontal" align="start" space={16}>
    <div style={{ width: pxToRem(120), flexShrink: 0 }}>
      <Text font="semibold14" text={title} />
    </div>
    <div>{children}</div>
  </Stack>
);

interface MCPServerJourneySidebarProps {
  serverId: string;
  existingJourney?: MCPServerJourney;
}

const IconRenderer = ({
  iconName,
  color,
  size = 12,
}: {
  iconName: keyof typeof ICON_CHOOSER_ICONS;
  color: Color;
  size?: number;
}) => {
  const IconComponent = ICON_CHOOSER_ICONS[iconName] ?? ICON_CHOOSER_ICONS.IconAI;
  return (
    <IconFancyContainer
      size="sm"
      icon={<IconComponent size={size} color={color} />}
      variant={COLOR_TO_VARIANT[color]}
    />
  );
};

interface JourneySelectionStepProps {
  journeys: JourneyType[];
  onSelect: (journey: JourneyType) => void;
  isLoading: boolean;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  getWorkspaceData: (workspaceId: string) => { icon: string; color: Color; title: string };
}

const JourneySelectionStep = ({
  journeys,
  onSelect,
  isLoading,
  searchQuery,
  onSearchChange,
  getWorkspaceData,
}: JourneySelectionStepProps) => {
  const { t } = useScopedTranslate(locale);

  return (
    <Sidebar.Content>
      <Stack space={4}>
        <Sidebar.SimpleHeader title={t('addJourney')} />
        <SearchBox placeholder={t('search')} value={searchQuery} onChange={onSearchChange} />
        {isLoading ? (
          <Spinner />
        ) : (
          <Stack space={1}>
            {journeys.map((journey: JourneyType) => {
              const workspaceData = getWorkspaceData(journey.workspace);
              return (
                <Card key={journey.shortId} variant="transparent" space={2}>
                  <Stack orientation="horizontal" align="center" space={2}>
                    <div style={{ flex: 1 }}>
                      <Text font="semibold14" text={journey.title} />
                      {workspaceData.title && (
                        <Stack orientation="horizontal" align="center" space={1}>
                          <IconRenderer
                            iconName={workspaceData.icon as keyof typeof ICON_CHOOSER_ICONS}
                            color={workspaceData.color}
                            size={12}
                          />
                          <Text font="regular12" text={workspaceData.title} color="textSecondary" />
                        </Stack>
                      )}
                      {journey.description && (
                        <Text font="regular14" text={journey.description} color="textSecondary" />
                      )}
                    </div>
                    <Button
                      iconLeft={<IconAddPlus />}
                      variant="ghost"
                      text={t('core.add')}
                      onClick={() => onSelect(journey)}
                    />
                  </Stack>
                </Card>
              );
            })}
            {journeys.length === 0 && <EmptySearchState />}
          </Stack>
        )}
      </Stack>
    </Sidebar.Content>
  );
};

interface JourneyAttribute {
  shortId: string;
  description: string;
  required: boolean;
  show: boolean;
  attributeType: string;
  title: string;
  isCustomized: boolean;
}

const MCPServerJourneySidebar = ({ serverId, existingJourney }: MCPServerJourneySidebarProps) => {
  const { t } = useScopedTranslate(locale);
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearchQuery = useDebouncedValue(searchInput, 300);

  const { data: existingJourneys } = useMCPServerJourneys(serverId);
  const { data: workspaces } = useWorkspaces();

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    if (debouncedSearchQuery) {
      params.set('search', debouncedSearchQuery);
    }
    // Order by most recently updated and limit to 10
    params.set('ordering', '-date_updated');
    params.set('page_size', '10');

    // Exclude existing journeys for this server
    const existingJourneyIds = (existingJourneys || []).map((j) => j.journey);
    if (existingJourneyIds.length > 0) {
      params.set('exclude_journeys', existingJourneyIds.join(','));
    }

    return `?${params.toString()}`;
  }, [debouncedSearchQuery, existingJourneys]);

  const { data: journeys, isLoading } = useJourneys(queryString);

  const [step, setStep] = useState<'select-journey' | 'configure-details'>(
    existingJourney ? 'configure-details' : 'select-journey'
  );
  const [selectedJourney, setSelectedJourney] = useState<JourneyType | null>(null);

  const createMCPServerJourneyMutation = useCreateMCPServerJourney();
  const updateMCPServerJourneyMutation = useUpdateMCPServerJourney();

  const { data: selectedJourneyDetails, isLoading: isJourneyLoading } = useJourney(
    selectedJourney?.shortId ?? ''
  );

  const showAttributeModal = useAttributeModal();

  const [attributes, setAttributes] = useState<JourneyAttribute[]>([]);

  const getWorkspaceData = (workspaceId: string) => {
    const workspace = workspaces?.find((workspace: Workspace) => workspace.shortId === workspaceId);
    return {
      icon: workspace?.icon ?? 'IconWorkspace',
      color: (workspace?.color || 'green100') as Color,
      title: workspace?.title || '',
    };
  };

  useEffect(() => {
    if (existingJourney) {
      setAttributes(
        (existingJourney.attributes || []).map((attr) => ({
          shortId: attr.attributeShortId,
          description: attr.description || attr.title || '',
          required: attr.isRequired ?? false,
          show: attr.isShown ?? true,
          attributeType: attr.attributeType || '',
          title: attr.title || '',
          isCustomized: attr.isCustomized || false,
        }))
      );
    } else if (selectedJourneyDetails) {
      setAttributes(
        ((selectedJourneyDetails as JourneyType)?.attributes || []).map((attr: AttributeType) => ({
          shortId: attr.shortId,
          description: attr.title || '',
          required: false,
          show: true,
          attributeType: attr.attributeType || '',
          title: attr.title || '',
          isCustomized: false,
        }))
      );
    } else {
      setAttributes([]);
    }
  }, [existingJourney, selectedJourneyDetails]);

  const handleSelectJourney = (journey: JourneyType) => {
    setSelectedJourney(journey);
    setStep('configure-details');
  };

  const handleAttributeCardClick = (index: number) => {
    const attr = attributes[index];
    showAttributeModal({
      attribute: {
        description: attr.description || attr.title || '',
        required: attr.required ?? false,
        show: attr.show ?? true,
      },
      onSave: (updated: { description: string; required: boolean; show: boolean }) => {
        setAttributes((prev) => prev.map((a, idx) => (idx === index ? { ...a, ...updated } : a)));
      },
    });
  };

  const handleNameEmailClick = (field: 'name' | 'email') => {
    const fieldData = {
      name: { title: t('name'), description: t('nameDescription') },
      email: { title: t('email'), description: t('emailDescription') },
    }[field];

    showAttributeModal({
      attribute: {
        description: fieldData.description,
        required: true,
        show: true,
      },
      readOnly: true,
    });
  };

  if (step === 'select-journey') {
    return (
      <Sidebar>
        <JourneySelectionStep
          journeys={journeys || []}
          onSelect={handleSelectJourney}
          isLoading={isLoading}
          searchQuery={searchInput}
          onSearchChange={setSearchInput}
          getWorkspaceData={getWorkspaceData}
        />
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <Sidebar.SimpleHeader title={existingJourney ? t('editJourney') : t('addJourney')} />
      <Sidebar.Content>
        <Stack space={6} style={{ height: '100%' }}>
          {isJourneyLoading && !existingJourney ? (
            <Spinner />
          ) : (
            <Stack space={6}>
              <SectionLayout title={t('details')}>
                <Form
                  space={0}
                  onSubmit={async (values: any) => {
                    const journeyId = existingJourney?.journey || selectedJourney?.shortId;
                    if (!journeyId) return;

                    const payload = {
                      server: serverId,
                      journey: journeyId,
                      attributes: attributes.map((attr) => ({
                        attributeShortId: attr.shortId,
                        description: attr.description ?? attr.title ?? '',
                        isShown: attr.show ?? true,
                        isRequired: attr.required ?? false,
                      })),
                      description: values.description,
                    };
                    if (existingJourney?.id) {
                      await updateMCPServerJourneyMutation.mutateAsync({
                        id: existingJourney.id,
                        ...payload,
                      });
                    } else {
                      await createMCPServerJourneyMutation.mutateAsync(payload);
                    }
                    hideSidebar();
                  }}
                >
                  <Form.Textarea
                    id="description"
                    label={t('description')}
                    placeholder={t('descriptionPlaceholder')}
                    validators={['required']}
                    style={{ minHeight: pxToRem(120) }}
                    defaultValue={existingJourney?.description ?? ''}
                  />
                  <Text font="regular14" color="textSecondary" text={t('descriptionHelp')} />
                  <Sidebar.Footer>
                    <Form.Actions>
                      <Form.Reset
                        text={t('core.cancel')}
                        variant="secondary"
                        onClick={hideSidebar}
                      />
                      <Form.Submit
                        text={existingJourney ? t('core.save') : t('addJourney')}
                        variant="primary-blue"
                      />
                    </Form.Actions>
                  </Sidebar.Footer>
                </Form>
              </SectionLayout>

              <Separator />

              <SectionLayout title={t('journeyDataFields')}>
                <Stack space={2}>
                  <Text font="regular14" color="textSecondary" text={t('journeyFieldsHelp')} />
                  <Stack space={2}>
                    <Card
                      variant="transparent"
                      space={3}
                      onClick={() => handleNameEmailClick('name')}
                      style={{ cursor: 'pointer' }}
                    >
                      <Stack orientation="horizontal" align="center" space={2}>
                        <AttributeIcon type="text" color="elementPurple80" />
                        <Text font="semibold14" text="Name" />
                        <InfoTooltip tooltip={t('nameDescription')} />
                        <div style={{ marginLeft: 'auto' }}>
                          <IconVisibilityShown size={16} color="textSecondary" />
                        </div>
                      </Stack>
                    </Card>
                    <Card
                      variant="transparent"
                      space={3}
                      onClick={() => handleNameEmailClick('email')}
                      style={{ cursor: 'pointer' }}
                    >
                      <Stack orientation="horizontal" align="center" space={2}>
                        <AttributeIcon type="email" color="elementPurple80" />
                        <Text font="semibold14" text="Email" />
                        <InfoTooltip tooltip={t('emailDescription')} />
                        <div style={{ marginLeft: 'auto' }}>
                          <IconVisibilityShown size={16} color="textSecondary" />
                        </div>
                      </Stack>
                    </Card>
                    {attributes.map((attr, idx) => (
                      <Card
                        key={attr.shortId || idx}
                        variant="transparent"
                        space={3}
                        onClick={() => handleAttributeCardClick(idx)}
                        style={{ cursor: 'pointer' }}
                      >
                        <Stack orientation="horizontal" align="center" space={2}>
                          <AttributeIcon
                            type={attr.attributeType as AttributeFieldType}
                            color="elementPurple80"
                          />
                          <Text font="semibold14" text={attr.title || attr.description || ''} />
                          <InfoTooltip tooltip={attr.description || attr.title || ''} />
                          <div style={{ marginLeft: 'auto' }}>
                            {attr.show ? (
                              <IconVisibilityShown size={16} color="textSecondary" />
                            ) : (
                              <IconVisibilityHidden size={16} color="textSecondary" />
                            )}
                          </div>
                        </Stack>
                      </Card>
                    ))}
                  </Stack>
                </Stack>
              </SectionLayout>
            </Stack>
          )}
        </Stack>
      </Sidebar.Content>
    </Sidebar>
  );
};

const locale = {
  en: {
    addJourney: 'Add Journey',
    editJourney: 'Edit Journey',
    search: 'Search',
    noJourneys: 'No Journeys',
    details: 'Details',
    journeyDataFields: 'Journey data fields',
    description: 'Description',
    descriptionPlaceholder:
      "E.g., 'Initiates onboarding for new employees, collecting personal info, setting up system accounts, assigning orientation tasks, and scheduling HR welcome meetings.'",
    descriptionHelp:
      'Provide a clear, specific description of this journey. The AI uses this to select the correct workflow, so accuracy and clarity are essential. Using AI assistance is recommended.',
    journeyFieldsHelp:
      'List the essential data fields the AI will request from users when starting this journey. Clearly label and describe each field for accuracy.',
    name: 'Traveller name',
    nameDescription: 'Full name of the traveller',
    email: 'Traveller email',
    emailDescription: 'Email address of the traveller',
    noJourneysFound: 'No journeys found',
  },
};
